const DEFAULT_LOCAL_API_URL = 'http://localhost:3000';
const DEFAULT_PUBLIC_ROOT_DOMAIN = 'dev.ayeldo.com';
const getImportMetaEnv = () => {
    try {
        // Explicitly reference each VITE_* variable so Vite's static analysis
        // can replace them at build time. Accessing import.meta.env as a whole
        // object or via indirection breaks Vite's pattern matching.
        const apiUrl = import.meta.env.VITE_API_URL;
        const rootDomain = import.meta.env.VITE_PUBLIC_ROOT_DOMAIN;
        // import.meta.env values can be booleans or strings depending on the
        // environment (build-time replacement vs test harness). Normalize DEV to
        // a boolean where possible so truthiness checks behave consistently.
        const metaEnv = import.meta;
        const rawDev = metaEnv.env?.DEV;
        let dev;
        if (typeof rawDev === 'boolean')
            dev = rawDev;
        else if (typeof rawDev === 'string')
            dev = rawDev === 'true';
        else
            dev = undefined;
        return {
            ...(apiUrl !== undefined ? { VITE_API_URL: apiUrl } : {}),
            ...(rootDomain !== undefined ? { VITE_PUBLIC_ROOT_DOMAIN: rootDomain } : {}),
            ...(dev !== undefined ? { DEV: dev } : {}),
        };
    }
    catch (error) {
        // Accessing import.meta can throw during SSR/Node builds. Log the error
        // so callers and test environments can observe failures instead of
        // silently swallowing them.
        // Keep returning undefined to preserve previous behavior when import.meta
        // is not available, but surface the problem via a warning.
        // eslint-disable-next-line no-console
        console.warn('Could not access import.meta.env; falling back to defaults.', error);
        return undefined;
    }
};
const resolveSourceEnv = (env) => {
    if (env) {
        return env;
    }
    return getImportMetaEnv();
};
// Named predicate to clarify intent when checking for a dev environment.
const isDevEnvironment = (source) => {
    return source?.DEV === true;
};
// Try resolving when callers explicitly provided an `env` argument.
// Keeps the original precedence: configured VITE_API_URL -> DEV fallback -> browser origin.
const resolveExplicitProvidedUrl = (source, configured) => {
    if (configured !== undefined && configured !== '')
        return configured;
    if (isDevEnvironment(source))
        return DEFAULT_LOCAL_API_URL;
    try {
        if (typeof window !== 'undefined') {
            const w = window;
            if (typeof w.location?.origin === 'string' && w.location.origin !== '') {
                return w.location.origin;
            }
        }
    }
    catch (error) {
        // eslint-disable-next-line no-console
        console.warn('Could not read window.location.origin while resolving API base URL', error);
    }
    return undefined;
};
// Try resolving when no explicit env was provided (implicit runtime path).
// Prefer the page origin when available.
const resolveImplicitRuntimeUrl = () => {
    try {
        if (typeof window !== 'undefined') {
            const w = window;
            if (typeof w.location?.origin === 'string' && w.location.origin !== '') {
                return w.location.origin;
            }
        }
    }
    catch (error) {
        // eslint-disable-next-line no-console
        console.warn('Could not read window.location.origin while resolving API base URL', error);
    }
    return undefined;
};
// Fallback decision: prefer baked VITE_API_URL, then DEV local URL, otherwise error.
const resolveFallbackUrl = (source, configured) => {
    if (configured !== undefined && configured !== '')
        return configured;
    if (isDevEnvironment(source))
        return DEFAULT_LOCAL_API_URL;
    throw new Error('VITE_API_URL is not configured for production build');
};
// Internal core that accepts an explicit callerProvided flag so the orchestration
// logic is obvious and testable. This avoids scattering `arguments.length` checks
// through the decision logic.
const resolveApiBaseUrlCore = (env, callerProvided) => {
    const source = resolveSourceEnv(env);
    const configured = source?.VITE_API_URL?.trim();
    if (callerProvided) {
        const explicit = resolveExplicitProvidedUrl(source, configured);
        if (explicit !== undefined)
            return explicit;
        // Fall through to consistent error path when explicit resolution fails.
        throw new Error('VITE_API_URL is not configured for production build');
    }
    const implicit = resolveImplicitRuntimeUrl();
    if (implicit !== undefined)
        return implicit;
    return resolveFallbackUrl(source, configured);
};
export function resolveApiBaseUrl(env) {
    // Wrapper function: a single place performs the "caller provided" detection
    // and forwards the explicit boolean to the core resolver. Call sites can be
    // migrated to the core if they need to explicitly indicate presence.
    const callerProvided = arguments.length > 0 && env !== undefined;
    return resolveApiBaseUrlCore(env, callerProvided);
}
export const resolvePublicRootDomain = (env) => {
    const source = resolveSourceEnv(env);
    const trimmed = source?.VITE_PUBLIC_ROOT_DOMAIN?.trim();
    return trimmed !== undefined && trimmed !== '' ? trimmed : DEFAULT_PUBLIC_ROOT_DOMAIN;
};
// Canonical API path prefix used by all frontends
export const API_PATH_PREFIX = '/api/v1';
// Resolve the full API URL including the canonical path prefix.
// Delegates to resolveApiBaseUrl for base URL selection and appends the prefix.
export const resolveApiUrl = (env) => {
    return resolveApiBaseUrl(env) + API_PATH_PREFIX;
};
